<template>
  <Modal
    :title="editModalTitle"
    bottom-close-button-label="保存"
    :isCloseButtonDisabled="isSaveButtonDisabled"
    @close="save"
    @dismiss="$emit('close')"
  >
    <template v-slot:body>
      <div class="edit-modal-content">
        <div class="row1" v-if="canUpdateTs">
          <div class="custom-control custom-checkbox mb0 mr16 mt6 update-ts-checkbox">
            <input
              type="checkbox"
              id="update_ts"
              v-model="state.editing.update_ts"
              class="custom-control-input"
            >
            <label class="custom-control-label" for="update_ts">
              更新を通知
            </label>
          </div>
        </div>
        <div class="row2 form-group">
          <label class="form-control-label">種別</label>
          <select class="form-control" v-model="state.editing.commentType">
            <option v-for="e in masters.comment.commentTypes" :key="e.commentType" :value="e.commentType">
              {{e.commentTypeDisp}}
            </option>
          </select>
        </div>
        <div class="row3 form-group">
          <label class="form-control-label">公開範囲</label>
          <select class="form-control" v-model="state.editing.shareScope">
            <option v-for="e in masters.lov.shareScopes" :key="e.key" :value="e.key">
              {{e.val}}
            </option>
          </select>
        </div>
        <div class="row3b form-group" v-if="hasDamageRank">
          <label class="form-control-label">判定</label>
          <select class="form-control" v-model="state.editing.damageRank">
            <option :value="null" />
            <option v-for="code in COMMENT_DAMAGE_RANK_CODES" :key="code" :value="code">{{code}}</option>
          </select>
        </div>
        <div class="row4 form-group">
          <span class="lbl ws-nowrap">場所</span>
          <select
            class="custom-select form-control ml4 mr4"
            v-model="state.editing.kpUid"
            :disabled="nearKps.length === 0"
          >
            <option value=""></option>
            <option v-for="(e, idx) in nearKps" :key="idx" :value="e.kpUid">
              {{ e.locationDisp }}
            </option>
          </select>
          <span class="lbl ws-nowrap">方角</span>
          <input
            v-model="state.editing.angle"
            type="number"
            min="-180"
            max="180"
            class="ib form-control ta-right angle ml4"
          />
          <span class="ib ml2">°</span>
          <div class="angle-arrow-wrap ml6" v-if="editItemHasValidAngle">
            <div class="angle-arrow-bg">
            </div>
            <div class="angle-arrow-img" :style="{ transform: `rotateZ(${state.editing.angle}deg)`}">
            </div>
          </div>
        </div>
        <div class="row5 form-group">
          <label class="form-control-label">内容</label>
          <textarea
            class="form-control"
            v-model="state.editing.content"
            :rows="state.editing.contentTextareaRows"
          >
          </textarea>
        </div>
        <div class="row6">
          <PreviewFilesArea
            :preview-files="sortedPreviewFiles"
            :is-changeable="true"
            :accepted-file-types="ACCEPTED_FILE_TYPES"
            @file-upload="handleFileUpload"
            @remove-file="removeFile"
          />
        </div>
        <div class="form-group errors">
          <div class="error" v-for="(e, i) in errorMsgs" :key="i">
            {{e}}
          </div>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, reactive } from 'vue';
import type { Comment } from 'src/models/comment';
import type { AdminCommentUpdateRequest } from 'src/models/apis/comment/adminCommentRequest';
import type { CandidateFile } from 'src/models';
import type { GIKp } from 'src/models/geoItem';
import {
  hasDamageRankFeature,
  getNearKps,
  validateAngle,
  validateComment,
  fetchFileAsObjectInfo,
  ATTACHMENT_FILE_MAX_MB_DEFAULT,
  MAX_SAVABLE_FILES_COUNT,
  ACCEPTED_FILE_TYPES,
} from 'src/lib/commentHelper';
import { getErrorMessages } from 'src/lib/errMsgHelper';
import type { AxiosError } from 'axios';
import { splitByLineBreak } from 'src/lib/utils';
import { convertFilesToFileObjs, type FileObj } from 'src/lib/fileHelper';
import { useReactiveMasters } from 'src/composables/useMasters';
import PreviewFilesArea from 'src/components/lib/PreviewFilesArea/index.vue';
import { COMMENT_DAMAGE_RANK_CODES, type CommentDamageRank } from 'src/consts/comment_damage_rank';
import { COMMENT_SOURCE_TYPE_SELF } from 'src/consts/comment_source_type';

const NEAR_KP_THRESHOLD_METERS = 100;

const props = defineProps<{
  editingComment: Comment;
  isRequesting: boolean;
  httpErrorMsgs: string[];
}>();

const emit = defineEmits<{
  (e: 'update', id: number, updateData: AdminCommentUpdateRequest): void;
  (e: 'close'): void;
}>();

interface CommentEditForm {
  commentType: string;
  content: string | null;
  shareScope: number | null;
  angle: number | null;
  update_ts: boolean;
  damageRank: CommentDamageRank | null;
  contentTextareaRows: number;
  kpUid: string | null;
  lat: string;
  lon: string;
  saveFiles: FileObj[];
  removeFileIds: number[];
}

const buildEditForm = (comment: Comment): CommentEditForm => {
  const minRows = 4;
  const rows = Math.max(splitByLineBreak(comment.content).length, minRows);
  return {
    commentType: comment.commentType,
    content: comment.content,
    contentTextareaRows: rows,
    shareScope: comment.shareScope,
    angle: comment.angle,
    kpUid: comment.kpUid,
    lat: comment.location.lat,
    lon: comment.location.lon,
    update_ts: false,
    damageRank: comment.damageRank,
    saveFiles: [],
    removeFileIds: [],
  };
};

const state = reactive<{ editing: CommentEditForm; candidateFiles: CandidateFile[]; errorMsgs: string[] }>({
  editing: buildEditForm(props.editingComment),
  candidateFiles: [],
  errorMsgs: [],
});

const { masters } = useReactiveMasters();

const editModalTitle = '編集';

const errorMsgs = computed<string[]>(() => [...props.httpErrorMsgs, ...state.errorMsgs]);

const isSaveButtonDisabled = computed<boolean>(() => {
  return props.isRequesting || !validateComment(state.editing) || state.errorMsgs.length > 0;
});
const nearKps = computed<GIKp[]>(() => {
  if (!state.editing.lat || !state.editing.lon) return [];

  return getNearKps(
    { lat: state.editing.lat, lon: state.editing.lon },
    masters.kp.kpMap,
    masters.kp.roadNameDispMap,
    NEAR_KP_THRESHOLD_METERS,
  );
});
const editItemHasValidAngle = computed<boolean>(() => {
  return validateAngle(state.editing.angle);
});
const sortedPreviewFiles = computed<CandidateFile[]>(() => {
  return [...state.candidateFiles].sort((a, b) => a.id - b.id);
});
const canUpdateTs = computed<boolean>(() => {
  return props.editingComment.sourceType === COMMENT_SOURCE_TYPE_SELF;
});
const hasDamageRank = computed<boolean>(() => {
  return hasDamageRankFeature(masters.comment.commentFeaturesBySourceType, props.editingComment.sourceType);
});

const releaseAllFiles = (): void => {
  state.editing.saveFiles.forEach(elm => URL.revokeObjectURL(elm.content));
  state.candidateFiles.forEach(elm => URL.revokeObjectURL(elm.content));
};

const toPayload = (): AdminCommentUpdateRequest => {
  return {
    lat: state.editing.lat ?? '',
    lon: state.editing.lon ?? '',
    movie_id: null,
    share_scope: state.editing.shareScope || 0,
    comment_type: state.editing.commentType,
    content: state.editing.content || '',
    kp_uid: state.editing.kpUid || null,
    angle: state.editing.angle,
    update_ts: state.editing.update_ts,
    save_files: state.editing.saveFiles.map(e => e.file),
    remove_file_ids: state.editing.removeFileIds,
    damage_rank: state.editing.damageRank,
  };
};

const save = (): void => {
  if (isSaveButtonDisabled.value) return;

  emit('update', props.editingComment.id, toPayload());
};

const handleFileUpload = async(fileList: FileList): Promise<void> => {
  state.errorMsgs = [];
  const uploadFilesCount = fileList.length + state.candidateFiles.length;
  try {
    if (uploadFilesCount > MAX_SAVABLE_FILES_COUNT) {
      throw Error('添付ファイルの最大数は' + MAX_SAVABLE_FILES_COUNT + '個です。');
    }
    // 新規ファイルのidを連番で割り振る(実際のidはサーバー側で振られる)
    const maxId = state.candidateFiles.map(e => e.id).reduce((a, b) => Math.max(a, b), 0);
    const fileObjs = await convertFilesToFileObjs(fileList, maxId, ATTACHMENT_FILE_MAX_MB_DEFAULT);
    state.candidateFiles.push(...fileObjs);
    state.editing.saveFiles.push(...fileObjs);
  } catch (error) {
    state.errorMsgs.push(...getErrorMessages(error as AxiosError));
  }
};
const removeFile = (fileId: number): void => {
  state.candidateFiles = state.candidateFiles.filter(e => e.id !== fileId);
  const saveFile = state.editing.saveFiles.find(e => e.id === fileId);
  if (saveFile) {
    state.editing.saveFiles = state.editing.saveFiles.filter(e => e.id !== fileId);
    return;
  }
  state.editing.removeFileIds.push(fileId);
};

onMounted(async() => {
  const commentFilePromises = props.editingComment.commentFiles.map<Promise<CandidateFile | null>>(async(file) => {
    const savedFileInfo = await fetchFileAsObjectInfo(props.editingComment.id, file.id);
    if (!savedFileInfo || !savedFileInfo.url || !savedFileInfo.type) return null;

    return {
      id: file.id,
      content: savedFileInfo.url,
      type: savedFileInfo.type,
      name: file.fileName ?? '',
      path: file.filePath,
      selected: true,
    };
  });
  state.candidateFiles = (await Promise.all(commentFilePromises))
    .filter((e): e is CandidateFile => e !== null);
});

onBeforeUnmount(() => {
  releaseAllFiles();
});
</script>

<style lang="scss" scoped>
.edit-modal-content {
  .row1 {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    .update-ts-checkbox {
      margin-left: auto;
      white-space: nowrap;
      font-size: 16px;
    }
  }

  .row4 {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    .custom-select {
      width: 280px;
    }

    .angle-arrow-wrap {
      display: inline-block;
      width: 32px;
      height: 32px;
      margin-top: -1px;
      position: relative;
      .angle-arrow-bg {
        position: absolute;
        width: 32px;
        height: 32px;
        top: 0;
        left: 0;
        background-image: url('/static/img/comment-angle-bg.png');
        background-size: cover;
      }
      .angle-arrow-img {
        position: absolute;
        width: 32px;
        height: 32px;
        top: 0;
        left: 0;
        background-image: url('/static/img/comment-angle.png');
        background-size: cover;
      }
    }

    .angle {
      width: 70px;
    }
  }
}
</style>
